import { performAuthFailureLogout } from "./authUtils";

/**
 * Maps WebSocket close codes to user-friendly messages.
 * Returns a generic fallback for unknown codes.
 */
export function mapWebSocketCloseCodeToMessage(code: number, serverReason?: string): string {
  // Server-supplied reasons are trusted for known non-auth errors.
  if (serverReason && code !== 1008) {
    return serverReason;
  }

  switch (code) {
    case 1000:
      return "Chat session ended.";
    case 1001:
      return "You navigated away. Reconnect to resume.";
    case 1002:
      return "Connection closed due to a protocol error.";
    case 1003:
      return "Received an unsupported message format.";
    case 1006:
      return "Connection lost. Please check your internet and try again.";
    case 1007:
      return "Received an invalid message from the server.";
    case 1008:
      return "Access denied. You are not authorised to use this chat.";
    case 1009:
      return "A message was too large to process.";
    case 1011:
      return "An unexpected server error occurred. Please try again.";
    case 1012:
      return "The server is restarting. Reconnecting shortly…";
    case 1013:
      return "The server is temporarily unavailable. Please try again.";
    case 4000:
      return "Your session has expired. Please sign in again.";
    case 4001:
      return "Access denied. You do not have permission to access this resource.";
    case 4003:
      return "Rate limit exceeded. Please wait before sending more messages.";
    default:
      return "Connection closed unexpectedly. Please try again.";
  }
}

/**
 * Handles websocket authentication errors from error messages.
 * Checks if the message indicates an authentication failure and triggers logout.
 * @param message - The error message from the websocket
 * @param type - The message type (should be "error" for auth failures)
 * @returns true if authentication error was handled (logout triggered), false otherwise
 */
export const handleWebSocketAuthError = (message: string, type: string): boolean => {
  // Check if this is an authentication error
  if (type !== "error" || message !== "Authentication failed") {
    return false;
  }

  performAuthFailureLogout();
  return true;
};

/**
 * Handles websocket authentication errors from close events.
 * Checks if the close code and reason indicate an authentication failure and triggers logout.
 * @param code - The websocket close code
 * @param reason - The websocket close reason (optional)
 * @returns true if authentication error was handled (logout triggered), false otherwise
 */
export const handleWebSocketCloseAuthError = (code: number, reason?: string): boolean => {
  // Check if this is an authentication error
  // Code 1008 is Policy Violation, used for auth failures
  // Note: 1008 is also used for "Access denied", so we must check the reason
  if (code !== 1008 || reason !== "Authentication failed") {
    return false;
  }

  performAuthFailureLogout();
  return true;
};
